/**
 * MultiSelectCheckboxField -- Dynamic multi-select checkbox field.
 *
 * Renders a set of checkboxes for selecting multiple values from a list.
 * Supports two layouts:
 *
 * 1. Flat list -- when only `checkbox_options` is provided.
 * 2. Grouped -- when `checkbox_groups` is provided, options are organized
 *    under group headers, each with its own Select All / Deselect All toggle.
 *
 * The field value is a `string[]` of selected option values.
 *
 * Uses the shadcn/ui Checkbox primitive and follows the same structural
 * conventions as TextField and BooleanField (label, help text, tooltip,
 * error display, ARIA attributes).
 */

import { useCallback, useEffect, useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SelectOption } from '../../../../../types/connector-schema';
import type { DynamicFieldProps } from './index';
import { isOptionVisible } from '../../../../../lib/validation/fieldVisibility';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve the current value to a string array. */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
}

/** Resolved group used for rendering. */
interface ResolvedGroup {
  label: string;
  items: SelectOption[];
}

// ---------------------------------------------------------------------------
// Sub-component: a single group of checkboxes with a Select All toggle
// ---------------------------------------------------------------------------

interface CheckboxGroupSectionProps {
  group: ResolvedGroup;
  selectedSet: Set<string>;
  onToggle: (optionValue: string) => void;
  onSelectAllGroup: (optionValues: string[]) => void;
  onDeselectAllGroup: (optionValues: string[]) => void;
  disabled?: boolean;
  /** Prefix for stable ARIA ids */
  idPrefix: string;
}

function CheckboxGroupSection({
  group,
  selectedSet,
  onToggle,
  onSelectAllGroup,
  onDeselectAllGroup,
  disabled,
  idPrefix,
}: CheckboxGroupSectionProps) {
  const groupValues = group.items.map((i) => i.value);
  const allSelected = groupValues.length > 0 && groupValues.every((v) => selectedSet.has(v));
  const someSelected = groupValues.some((v) => selectedSet.has(v));

  const headerId = `${idPrefix}-group-${group.label.replace(/\s+/g, '-').toLowerCase()}`;

  const handleSelectAllChange = useCallback(
    (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        onSelectAllGroup(groupValues);
      } else {
        onDeselectAllGroup(groupValues);
      }
    },
    [groupValues, onSelectAllGroup, onDeselectAllGroup],
  );

  return (
    <div role="group" aria-labelledby={headerId} className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 id={headerId} className="text-sm font-medium text-muted-foreground">
          {group.label}
        </h4>
      </div>

      {/* Select All checkbox for this group */}
      <label className="flex items-center gap-2 cursor-pointer pl-1">
        <Checkbox
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          onCheckedChange={handleSelectAllChange}
          disabled={disabled}
          aria-label={`Select all ${group.label}`}
        />
        <span className="text-sm select-none italic text-muted-foreground">Select All</span>
      </label>

      {/* Individual option checkboxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pl-1">
        {group.items.map((option) => {
          const optionId = `${idPrefix}-option-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className="flex items-center gap-2 cursor-pointer rounded-md p-1.5 hover:bg-accent/50 transition-colors"
            >
              <Checkbox
                id={optionId}
                checked={selectedSet.has(option.value)}
                onCheckedChange={() => onToggle(option.value)}
                disabled={disabled}
                aria-checked={selectedSet.has(option.value)}
              />
              <span className="text-sm select-none">{option.label}</span>
              {option.description && (
                <span className="text-xs text-muted-foreground">({option.description})</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MultiSelectCheckboxField({
  field,
  value,
  onChange,
  error,
  readOnly,
  allValues,
  dirty,
}: DynamicFieldProps) {
  const fieldId = `field-${field.name}`;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  const selected = useMemo(() => toStringArray(value), [value]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const allOptions: SelectOption[] = field.checkbox_options ?? [];

  // Per-option visibility: only keep options whose visible_if is satisfied.
  // Options with no visible_if are always kept.
  const options = useMemo(
    () => allOptions.filter((o) => isOptionVisible(o, allValues)),
    [allOptions, allValues],
  );
  const optionMap = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  // Prune selections whose option has become invisible (e.g. user changed
  // report_view). Run as an effect so React commits a stable render first,
  // then schedules the onChange; guarded against no-op updates.
  //
  // Only an option that EXISTS and is hidden is pruned. A stored value the
  // catalog does not offer at all is kept (#2000): pruning it ran on mount,
  // so merely opening a saved connector marked the field edited and the next
  // save of any field wrote the narrowed array -- the shape in which
  // `Meta Ads - CleanChoice` lost campaign_name/adset_name/ad_name.
  useEffect(() => {
    const visibleValueSet = new Set(options.map((o) => o.value));
    const knownValueSet = new Set(allOptions.map((o) => o.value));
    const filteredSelection = selected.filter(
      (v) => visibleValueSet.has(v) || !knownValueSet.has(v),
    );
    if (filteredSelection.length !== selected.length) {
      onChange(filteredSelection);
    }
  }, [options, allOptions, selected, onChange]);

  // Build resolved groups -------------------------------------------------
  const resolvedGroups: ResolvedGroup[] = useMemo(() => {
    const groups = field.checkbox_groups;
    if (!groups || Object.keys(groups).length === 0) {
      // No groups -- return a single flat group with an empty label
      return [{ label: '', items: options }];
    }

    const result: ResolvedGroup[] = [];
    const groupedValues = new Set<string>();

    for (const [groupLabel, values] of Object.entries(groups)) {
      const items = values
        .map((v) => optionMap.get(v))
        .filter((o): o is SelectOption => !!o);
      if (items.length > 0) {
        result.push({ label: groupLabel, items });
        values.forEach((v) => groupedValues.add(v));
      }
    }

    // Collect any options not assigned to a group
    const ungrouped = options.filter((o) => !groupedValues.has(o.value));
    if (ungrouped.length > 0) {
      result.push({ label: 'Other', items: ungrouped });
    }

    return result;
  }, [field.checkbox_groups, options, optionMap]);

  const hasGroups = resolvedGroups.length > 1 || resolvedGroups[0]?.label !== '';

  // Callbacks -------------------------------------------------------------
  const toggleItem = useCallback(
    (itemValue: string) => {
      const next = new Set(selectedSet);
      if (next.has(itemValue)) {
        next.delete(itemValue);
      } else {
        next.add(itemValue);
      }
      onChange(Array.from(next));
    },
    [selectedSet, onChange],
  );

  const selectAllGroup = useCallback(
    (groupValues: string[]) => {
      const next = new Set(selectedSet);
      groupValues.forEach((v) => next.add(v));
      onChange(Array.from(next));
    },
    [selectedSet, onChange],
  );

  const deselectAllGroup = useCallback(
    (groupValues: string[]) => {
      const next = new Set(selectedSet);
      groupValues.forEach((v) => next.delete(v));
      onChange(Array.from(next));
    },
    [selectedSet, onChange],
  );

  // Show the empty-state hint only when there is no validation error to
  // avoid duplicating the same message ("required" + "pick at least one") in
  // two visual styles (#183).
  const showHint = !error && selected.length === 0 && !!field.empty_hint;

  // ARIA described-by
  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
    showHint ? hintId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      {/* Label row */}
      <div className="flex items-center gap-1">
        <Label id={fieldId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {dirty && (
          <Badge variant="default" className="h-[18px] bg-[oklch(85%_0.13_75)] text-[oklch(35%_0.13_75)]">
            Edited
          </Badge>
        )}

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Summary count */}
        <span className="text-xs text-muted-foreground ml-auto">
          {selected.length} of {options.length} selected
        </span>
      </div>

      {/* Checkbox area */}
      <div
        role="group"
        aria-labelledby={fieldId}
        aria-describedby={describedBy}
        className="space-y-4"
      >
        {hasGroups ? (
          resolvedGroups.map((group) => (
            <CheckboxGroupSection
              key={group.label}
              group={group}
              selectedSet={selectedSet}
              onToggle={toggleItem}
              onSelectAllGroup={selectAllGroup}
              onDeselectAllGroup={deselectAllGroup}
              disabled={readOnly}
              idPrefix={fieldId}
            />
          ))
        ) : (
          /* Flat list with global Select All toggle */
          <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer rounded-md p-1.5 hover:bg-accent/50 transition-colors">
            <Checkbox
              checked={selected.length === options.length && options.length > 0}
              onCheckedChange={(checked) => {
                if (checked) {
                  selectAllGroup(options.map((o) => o.value));
                } else {
                  deselectAllGroup(options.map((o) => o.value));
                }
              }}
              disabled={readOnly}
              aria-label="Select all"
            />
            <span className="text-sm select-none italic text-muted-foreground">Select All</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {options.map((option) => {
              const optionId = `${fieldId}-option-${option.value}`;
              return (
                <label
                  key={option.value}
                  htmlFor={optionId}
                  className="flex items-center gap-2 cursor-pointer rounded-md p-1.5 hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    id={optionId}
                    checked={selectedSet.has(option.value)}
                    onCheckedChange={() => toggleItem(option.value)}
                    disabled={readOnly}
                    aria-checked={selectedSet.has(option.value)}
                  />
                  <span className="text-sm select-none">{option.label}</span>
                  {option.description && (
                    <span className="text-xs text-muted-foreground">({option.description})</span>
                  )}
                </label>
              );
            })}
          </div>
          </div>
        )}
      </div>

      {/* Empty-state hint -- surfaces opt-in semantics (#183) */}
      {showHint && (
        <p id={hintId} className="text-sm text-muted-foreground italic">
          {field.empty_hint}
        </p>
      )}

      {/* Help text */}
      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {/* Validation error */}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
